import { createSlice } from "@reduxjs/toolkit";
import userServices from "../services/userRoutes";

/* ----------------- UNIQUE KEY ----------------- */
function generateKey(item) {
  if (!item.selectedVariants || item.selectedVariants.length === 0) {
    return item._id;
  }

  const variantIds = item.selectedVariants
    .map(
      (v) =>
        v.variantValueId || `${v.variantKey}:${v.variantValue || ""}`.trim()
    )
    .sort();

  return `${item._id}-${variantIds.join("|")}`;
}

/* --------------------- LOAD LOCAL STORAGE --------------------- */
const loadInitialCart = () => {
  try {
    const raw = localStorage.getItem("cartItems");
    if (!raw) return [];
    const parsed = JSON.parse(raw);

    return parsed.map((item) => ({
      ...item,
      key: item.key || generateKey(item),
    }));
  } catch {
    return [];
  }
};

/* --------------------- INITIAL STATE --------------------- */
const initialState = {
  items: loadInitialCart(),
};

/* --------------------- LOCAL SAVE --------------------- */
const saveLocal = (items) =>
  localStorage.setItem("cartItems", JSON.stringify([...items]));

/* --------------------- SYNC TO BACKEND --------------------- */
const syncToDB = (items) => {
  const token = localStorage.getItem("accessToken");
  if (token) userServices.updateCart([...items]); // ⭐ FIXED
};

/* --------------------- SLICE --------------------- */
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const incoming = action.payload;
      const key = generateKey(incoming);

      const exists = state.items.find((p) => p.key === key);

      if (exists) {
        exists.qty += incoming.qty || 1;
      } else {
        state.items.push({
          ...incoming,
          key,
          qty: incoming.qty || 1,
        });
      }

      saveLocal(state.items);
      syncToDB(state.items);
    },

    increaseQty: (state, action) => {
      const key = action.payload;
      const item = state.items.find((p) => p.key === key);

      if (item) item.qty++;

      saveLocal(state.items);
      syncToDB(state.items);
    },

    decreaseQty: (state, action) => {
      const key = action.payload;
      const item = state.items.find((p) => p.key === key);

      if (item && item.qty > 1) {
        item.qty--;
      }

      saveLocal(state.items);
      syncToDB(state.items);
    },

    removeFromCart: (state, action) => {
      const key = action.payload;
      state.items = state.items.filter((p) => p.key !== key);

      saveLocal(state.items);
      syncToDB(state.items);
    },

    clearCart: (state) => {
      state.items = [];
      saveLocal([]);
      syncToDB([]);
    },

    /* ---------- MERGE AFTER LOGIN ---------- */
    mergeCartAfterLogin: (state, action) => {
      const dbCart = action.payload || [];
      let merged = [...dbCart];

      state.items.forEach((localItem) => {
        const exists = merged.find((x) => x.key === localItem.key);
        if (!exists) merged.push(localItem);
      });

      state.items = merged;

      saveLocal(state.items);
      syncToDB(state.items);
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseQty,
  decreaseQty,
  clearCart,
  mergeCartAfterLogin,
} = cartSlice.actions;

export default cartSlice.reducer;
